'use client'

import Image from 'next/image'
import {
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from 'react'
import { createPortal } from 'react-dom'

/**
 * Vanilla Smooth Modal (No third‑party UI or animation libs)
 * - Pure React + CSS (works great with Tailwind utility classes, but not required)
 * - Fade/blur backdrop + subtle scale/slide for the dialog
 * - Focus trap, ESC to close, click‑outside to close
 * - Body scroll lock while open
 * - Respects `prefers-reduced-motion`
 */

function useLockBodyScroll(locked: boolean) {
  useEffect(() => {
    const { overflow } = document.body.style
    if (locked) document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = overflow
    }
  }, [locked])
}

const subscribeNoop = () => () => {}

// true on the client, false during SSR and hydration
function useIsClient() {
  return useSyncExternalStore(
    subscribeNoop,
    () => true,
    () => false
  )
}

function trapFocus(container: HTMLElement, e: KeyboardEvent) {
  if (e.key !== 'Tab') return
  const focusables = container.querySelectorAll<HTMLElement>(
    'a[href], button, textarea, input, select, details,[tabindex]:not([tabindex="-1"])'
  )
  if (focusables.length === 0) return
  const first = focusables[0]
  const last = focusables[focusables.length - 1]
  const active = document.activeElement as HTMLElement | null
  if (e.shiftKey) {
    if (active === first || !container.contains(active)) {
      e.preventDefault()
      last.focus()
    }
  } else {
    if (active === last) {
      e.preventDefault()
      first.focus()
    }
  }
}

export function Modal({
  open,
  onOpenChange,
  title,
  children,
  size = 'md',
  closeOnOutsideClick = true,
}: {
  open: boolean
  onOpenChange: (v: boolean) => void
  title?: string
  children: ReactNode
  size?: 'sm' | 'md' | 'lg' | 'xl'
  closeOnOutsideClick?: boolean
}) {
  // Mount portal target only on client
  const mounted = useIsClient()
  const [visible, setVisible] = useState(open)
  const overlayRef = useRef<HTMLDivElement | null>(null)
  const panelRef = useRef<HTMLDivElement | null>(null)

  // Handle transition in/out: show immediately when opened (adjusting state
  // during render avoids an extra effect pass), hide after the exit animation
  if (open && !visible) setVisible(true)
  useEffect(() => {
    if (open) return
    // play exit animation then fully hide
    const t = setTimeout(() => setVisible(false), 200)
    return () => clearTimeout(t)
  }, [open])

  // Lock body scroll when open
  useLockBodyScroll(open)

  // Key handlers: ESC and TAB focus trap
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onOpenChange(false)
      if (panelRef.current) trapFocus(panelRef.current, e)
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, onOpenChange])

  if (!mounted) return null

  const widths: Record<typeof size, string> = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl',
  } as const

  // Nothing in DOM when closed and not animating
  if (!open && !visible) return null

  return createPortal(
    <div
      aria-hidden={!open}
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      ref={overlayRef}
      onMouseDown={(e) => {
        if (!closeOnOutsideClick) return
        // Close if clicking backdrop (not the panel)
        if (e.target === overlayRef.current) onOpenChange(false)
      }}
    >
      {/* Backdrop */}
      <div
        className={`absolute inset-0 bg-black/50 backdrop-blur-sm transition-opacity duration-200 ${
          open ? 'opacity-100' : 'opacity-0'
        }`}
      />

      {/* Panel */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title || 'Dialog'}
        ref={panelRef}
        className={
          `relative  ${widths[size]} rounded-lg border border-neutral-200 bg-white shadow-2xl outline-none transition-all duration-200 supports-[backdrop-filter]:bg-white/80 dark:border-neutral-800 dark:bg-white` +
          ` ` +
          (open
            ? 'opacity-100 translate-y-0 scale-100'
            : 'opacity-0 translate-y-2 scale-[0.98]')
        }
        style={{ willChange: 'transform, opacity' }}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        {/* {title ? (
          <div className="flex items-center justify-between gap-2 px-5 pt-5">
            <h2 className="text-lg font-semibold leading-none tracking-tight">
              {title}
            </h2>
            <button
              aria-label="Close"
              onClick={() => onOpenChange(false)}
              className="inline-flex h-9 w-9 items-center justify-center rounded-full hover:bg-black/5 focus:outline-none focus:ring-2 focus:ring-black/20 active:scale-95 dark:hover:bg-white/10 dark:focus:ring-white/20"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                className="h-5 w-5"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        ) : null} */}

        {/* Body */}
        <div className="sm:p-2 p-6 relative bg-white rounded-lg">
          <div
            className="text-black absolute top-4 right-4 cursor-pointer"
            onClick={() => {
              onOpenChange(false)
            }}
          >
            <Image
              src="/svgs/close.svg"
              alt="close-modal"
              width={24}
              height={24}
              priority
              //   className="object-cover -z-0"
            />
          </div>
          {children}
        </div>
      </div>
    </div>,
    document.body
  )
}
